// /protocols/:id — «Таблица протокола» (011, сценарии 2, 3 и 7): протокол в режиме ведения.
// Право на раздел — minutes.read (022: у внешнего оно есть по роли в протоколе без documents.read,
// research Р3); какие протоколы открываются и кто их правит, решает сервер по областям (FR-041,
// FR-042).
// Модуль таблицы (Tabulator, Tiptap) загружается только на этой странице (ADR-0035).
// 014 (T027; FR-004): ?item=<номер пункта> — переход из «Контроля» к пункту таблицы. Параметр
// читает страница таблицы (дорожка W4); чужое значение отбрасывается.
// 030 (T039; FR-023; ASG-DESK-06): ?assignee=<номер записи человека> — «Открыть в протоколе» из
// «Моих задач»: таблица открывается с быстрым фильтром «Ответственные» на этом человеке (вместе
// с переходом к item); после применения страница снимает оба параметра, как item.
// 0.26.1 (ACC-07): ?readiness=mailing — переход «Открыть рассылку П-N» из причины тени письма:
// окно «Готовность к рассылке» открывается на вкладке «Рассылка», если оно доступно; параметр
// снимается, как item.
// 045 (OPN-01, OPN-03, OPN-04; research Р1, Р2): страница — прямым импортом: autoCodeSplitting
// кладёт её в чанк маршрута, и наведение на ссылку или строку «Документов» грузит его до нажатия
// (модуль таблицы по-прежнему грузится только для этого маршрута, ADR-0035). Данные читает
// загрузчик (features/minutes/load.ts): маршрутизатор держит прежнюю страницу до ответа, после
// 300 мс — скелет протокола (pendingComponent). Перезагрузка устаревшего загрузчика — с ожиданием
// (staleReloadMode: 'blocking'): после предзагрузки наведением переход тоже ждёт данных, а не
// рисует страницу без них.
import { IdSchema } from '@kp/contracts';
import { createFileRoute } from '@tanstack/react-router';
import { ProtocolPage } from '../../features/minutes/ProtocolPage.tsx';
import { ProtocolSkeleton } from '../../features/minutes/ProtocolSkeleton.tsx';
import { protocolLoader } from '../../features/minutes/load.ts';
import { requireSection } from '../../shell/permissions.ts';

interface ProtocolSearch {
  /** Пункт, к которому перейти в таблице. */
  item?: string | undefined;
  /** Запись человека справочника: начальный быстрый фильтр «Ответственные» (030). */
  assignee?: string | undefined;
  /** Окно готовности на вкладке «Рассылка» (0.26.1, ACC-07). */
  readiness?: 'mailing' | undefined;
}

/**
 * Чужое значение — undefined явно: корневой маршрут пропускает параметры адреса как есть, и без
 * этого непроверенный item дошёл бы до страницы в общем наборе параметров.
 */
function parseProtocolSearch(search: Record<string, unknown>): ProtocolSearch {
  const item = IdSchema.safeParse(search.item);
  const assignee = IdSchema.safeParse(search.assignee);
  return {
    item: item.success ? item.data : undefined,
    assignee: assignee.success ? assignee.data : undefined,
    readiness: search.readiness === 'mailing' ? 'mailing' : undefined,
  };
}

export const Route = createFileRoute('/_app/protocols/$id')({
  validateSearch: parseProtocolSearch,
  beforeLoad: requireSection('protocol'),
  staticData: { crumb: 'Таблица протокола' },
  loader: { handler: protocolLoader, staleReloadMode: 'blocking' },
  pendingComponent: ProtocolPending,
  component: ProtocolRoute,
});

function ProtocolRoute() {
  const { id } = Route.useParams();
  return <ProtocolPage key={id} id={id} />;
}

/** Данных нет дольше 300 мс от нажатия — один скелет протокола (OPN-04). */
function ProtocolPending() {
  const { id } = Route.useParams();
  return <ProtocolSkeleton id={id} delay={0} />;
}
